import type { BraceletPatternDto, RowReadingDto, RowRunDto } from '@miyuki/types';

import { assertRowIndex } from './pattern.entity';

/**
 * El resumen de una fila: sus celdas agrupadas en tramos, en el orden en que se
 * ensartan.
 *
 * Leer la cuadrícula obliga a contar celda por celda —«azul, azul, negro,
 * azul»—; el dato exacto ya está y se puede decir como se trabaja: «dos azules,
 * una negra, una azul» (RF-27).
 */

/**
 * `true` si la fila se ensarta desde el extremo derecho.
 *
 * La fila 1 arranca siempre por la derecha (RF-28). Lo elegible es solo si el
 * sentido alterna de fila en fila —al terminar una fila el hilo queda en el
 * extremo opuesto, así que la siguiente vuelve por ahí— o si todas se leen
 * igual (RF-28a, RF-28b).
 */
export function readsRightToLeft(pattern: BraceletPatternDto, row: number): boolean {
  return !pattern.readingAlternates || row % 2 === 0;
}

/**
 * Agrupa las celdas de la fila en tramos de contenido igual.
 *
 * Las celdas vacías contiguas forman su propio tramo, señalado como hueco y no
 * como color (RF-30a): sin esa regla la fila `A · vacía · A` se resumiría como
 * dos mostacillas seguidas del mismo color y se ensartaría corrida.
 *
 * De la construcción salen dos propiedades que los tests fijan: la suma de los
 * `count` es el ancho del diseño (RF-30b) y recorrer los tramos reproduce la
 * fila celda por celda (RF-29).
 */
export function readRow(pattern: BraceletPatternDto, row: number): RowReadingDto {
  assertRowIndex(pattern, row);

  const rightToLeft = readsRightToLeft(pattern, row);
  const cells = pattern.rows[row] ?? [];
  const ordered = rightToLeft ? [...cells].reverse() : cells;

  const runs: RowRunDto[] = [];
  for (const cell of ordered) {
    const last = runs[runs.length - 1];
    if (last?.value === cell) {
      runs[runs.length - 1] = { value: cell, count: last.count + 1 };
    } else {
      runs.push({ value: cell, count: 1 });
    }
  }

  return { runs, rightToLeft };
}
